import React from 'react';

import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import BorrowerForm from './BorrowerForm';
import CrudPageLayout from '../../../layouts/CrudPageLayout';

import BorrowerService from '../../../services/BorrowerService';
import { useUiStore } from '../../../store';

export default function Borrowers({
  isAddBorrowerFormVisible,
  setIsAddBorrowerFormVisible,
  isEdit,
}) {
  const { id } = useParams();

  const showDialogModal = useUiStore((state) => state.showDialogModal);
  const notify = useUiStore((state) => state.notify);

  const { data: borrowers, refetch } = useQuery({
    queryKey: ['borrowers', id],
    queryFn: () => BorrowerService.getBorrowersOfLoanFile(id),
    enabled: !!id,
  });

  // console.log(borrowers);

  const { mutate: createBorrower, isPending: isCreating } = useMutation({
    mutationFn: (formValues) =>
      BorrowerService.createBorrower({ ...formValues, loanFileId: id }),
    onSuccess: async () => {
      console.log('Borrower created successfully');
      await refetch();
      setIsAddBorrowerFormVisible(false);
      notify({ type: 'success', message: 'Borrower created successfully' });
    },
    onError: (error) => {
      console.log(error);
      notify({ type: 'error', message: 'Error creating borrower' });
    },
  });

  const { mutate: updateBorrower, isPending: isUpdating } = useMutation({
    mutationFn: ({ formValues, borrowerId }) =>
      BorrowerService.updateBorrower({ formValues, id: borrowerId }),
    onSuccess: () => {
      console.log('Borrower updated successfully');
      refetch();
      notify({ type: 'success', message: 'Borrower updated successfully' });
    },
    onError: (error) => {
      console.log(error);
      notify({ type: 'error', message: 'Error updating borrower' });
    },
  });

  const { mutate: deleteBorrower, isPending: isDeleting } = useMutation({
    mutationFn: (borrowerId) => BorrowerService.deleteBorrower(borrowerId),
    onSuccess: async () => {
      console.log('Borrower deleted successfully');
      refetch();
      notify({ type: 'success', message: 'Borrower deleted successfully' });
    },
    onError: (error) => {
      console.log(error);
      notify({ type: 'error', message: 'Error deleting borrower' });
    },
  });

  const isPending = isCreating || isUpdating || isDeleting;

  return (
    <CrudPageLayout
      title={isEdit ? 'Edit Loan File' : 'Add Loan File'}
      addButtonLabel="Add Borrower"
      addButtonOnClick={() => setIsAddBorrowerFormVisible(true)}
    >
      <div className="flex flex-col gap-15">
        {isAddBorrowerFormVisible && (
          <BorrowerForm
            createBorrower={createBorrower}
            onDelete={() => setIsAddBorrowerFormVisible(false)}
            isPending={isPending}
          />
        )}
        {borrowers?.length ? (
          borrowers.map((borrower) => {
            return (
              <BorrowerForm
                key={borrower.id}
                borrower={borrower}
                updateBorrower={updateBorrower}
                onDelete={() =>
                  showDialogModal({
                    title: 'Delete Borrower',
                    text: 'Are you sure you want to delete this borrower?',
                    onConfirm: () => deleteBorrower(borrower.id),
                  })
                }
                isPending={isPending}
              />
            );
          })
        ) : (
          <p
            className={`text-gray-500 text-center text-lg mt-1 md:mt-5 ${isAddBorrowerFormVisible ? 'hidden' : ''}`}
          >
            There are no borrowers for this loan file. Click the button above to
            add a borrower
          </p>
        )}
      </div>
    </CrudPageLayout>
  );
}
